import { useMemo } from 'react';
import { IActivity, InputMode, DEFAULT_ALLOWED_MODES_BY_TYPE } from '@eduwrite/shared';

const FALLBACK_ALLOWED: InputMode[] = ['canvas'];
const FALLBACK_DEFAULT: InputMode = 'canvas';

/**
 * Extract allowed input modes from an activity config, falling back to
 * type-specific defaults when the teacher has not customised them.
 */
function getAllowedModes(activity: IActivity): InputMode[] {
    const cfg = activity.config;
    if (cfg?.allowedInputModes && Array.isArray(cfg.allowedInputModes) && cfg.allowedInputModes.length > 0) {
        return cfg.allowedInputModes;
    }
    return DEFAULT_ALLOWED_MODES_BY_TYPE[activity.type] || FALLBACK_ALLOWED;
}

/**
 * Extract the default input mode from an activity config, validating that
 * it is actually contained in the allowed set.
 */
function getDefaultMode(activity: IActivity, allowed: InputMode[]): InputMode {
    const cfg = activity.config;
    if (cfg?.defaultInputMode && allowed.includes(cfg.defaultInputMode)) {
        return cfg.defaultInputMode;
    }
    return allowed[0] || FALLBACK_DEFAULT;
}

export interface ActivityInputModes {
    allowedModes: InputMode[];
    defaultMode: InputMode;
}

/**
 * Hook that returns the input mode configuration for an activity.
 * Use this in every activity component that supports multi-modal input.
 *
 * Accessibility (WCAG 2.2 AA): When the user switches between input modes
 * (e.g. canvas → keyboard → voice), screen reader users need to be notified
 * of the selection change. Consuming components should use
 * `accessibilityLiveRegion="polite"` on the element that displays the current
 * mode so that the mode switch is announced to assistive technology. The
 * `defaultMode` return value indicates the initial mode and should be
 * announced on first render as well.
 */
export function useActivityInputModes(activity: IActivity): ActivityInputModes {
    return useMemo(() => {
        const allowedModes = getAllowedModes(activity);
        const defaultMode = getDefaultMode(activity, allowedModes);
        return { allowedModes, defaultMode };
    }, [activity]);
}
